import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { EEnergyNeedEditParameter } from "../../../../shared/oversight-core/enums/energy-need-edit-parameters";
import AppInput from "../../../../shared/oversight-core/ui-elements/input/app-input";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import RangeSlider from "../../../../shared/oversight-core/ui-elements/range-slider/range-slider";

interface IProps extends ModelContainerProps {
  type: EEnergyNeedEditParameter;
  currentValue: number | string;
  estimated?: number;
  unit?: string;
  onEditParameter: (type: EEnergyNeedEditParameter, value: string) => void;
}

const EditParameterModal = (props: IProps) => {
  const {
    show,
    onCancel,
    onClose,
    type,
    currentValue,
    estimated,
    unit,
    onEditParameter,
    ...rest
  } = props;

  const location = useLocation();
  const pathname = location.pathname;
  const isAdvanced = pathname.includes("advanced");
  const isAdvancedOrSimplified =
    pathname.includes("simplified") || pathname.includes("advanced");

  const [value, setValue] = useState<string>("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (show) {
      setValue(
        currentValue === 0 || currentValue === "Title Goes Here"
          ? ""
          : String(currentValue),
      );
      return;
    }

    setValue("");
  }, [currentValue, show]);

  const getTitle = (type: EEnergyNeedEditParameter) => {
    switch (type) {
      case EEnergyNeedEditParameter.TITLE:
        return "Edit Title";
      case EEnergyNeedEditParameter.MONTHLY_ENERGY_NEED:
        return "Edit Monthly Energy Need";
      case EEnergyNeedEditParameter.MAX_POWER:
        return "Edit Max Power";
      case EEnergyNeedEditParameter.DISCHARGE_RATE:
        return "Edit Discharge Rate";
      case EEnergyNeedEditParameter.NIGHT_LOAD:
        return "Edit Night Load";
      case EEnergyNeedEditParameter.SUNLIGHT_HOURS:
        return "Edit Daily Sunlight";
      case EEnergyNeedEditParameter.NO_OF_USERS:
        return "Edit No. of Users";
      case EEnergyNeedEditParameter.DEMAND_PER_USER:
        return "Edit Demand per User";
      case EEnergyNeedEditParameter.AUTONOMY_DAYS:
        return "Edit Autonomy Days";
      default:
        return "";
    }
  };

  const getView = (type: EEnergyNeedEditParameter) => {
    switch (type) {
      case EEnergyNeedEditParameter.TITLE:
        return (
          <Row className="mt-4">
            <Col>
              <AppInput
                name="title"
                placeholder="Title"
                errors={{
                  title: {
                    message: error,
                  },
                }}
                label="Title"
                value={value}
                onChange={(event) => {
                  if (!event.target.value.trim()) {
                    setError("Title cannot be empty.");
                  } else {
                    setError("");
                  }
                  setValue(event.target.value);
                }}
              />
            </Col>
          </Row>
        );
      case EEnergyNeedEditParameter.MONTHLY_ENERGY_NEED:
        return (
          <>
            {estimated && estimated > 0 && isAdvancedOrSimplified ? (
              <Row>
                <Col>
                  <Row>
                    <Col className="text-secondary font-size-12 font-weight-500">
                      {isAdvanced ? "Estimation By Schedule" : "Estimated"}
                    </Col>
                  </Row>
                  <Row>
                    <Col className="text-label font-size-24 font-weight-600">
                      {estimated.toFixed(2)}{" "}
                      <span className="font-size-16">{unit}</span>
                    </Col>
                  </Row>
                </Col>
              </Row>
            ) : (
              <></>
            )}
            <Row className="mt-4">
              <Col>
                <AppInput
                  placeholder={`${
                    isAdvancedOrSimplified ? "Additional " : ""
                  }Monthly Energy Need`}
                  type="number"
                  errors={Error}
                  label={`Add ${
                    isAdvancedOrSimplified ? "Additional " : ""
                  }Monthly Energy Need`}
                  isRequired={false}
                  value={value}
                  onChange={(event) => {
                    setValue(event.target.value);
                  }}
                />
              </Col>
            </Row>
          </>
        );
      case EEnergyNeedEditParameter.MAX_POWER:
        return (
          <>
            {estimated && estimated > 0 && isAdvancedOrSimplified ? (
              <Row>
                <Col>
                  <Row>
                    <Col className="text-secondary font-size-12 font-weight-500">
                      {isAdvanced ? "Estimation By Schedule" : "Estimated"}
                    </Col>
                  </Row>
                  <Row>
                    <Col className="text-label font-size-24 font-weight-600">
                      {estimated} <span className="font-size-16">{unit}</span>
                    </Col>
                  </Row>
                </Col>
              </Row>
            ) : (
              <></>
            )}
            <Row className="mt-4">
              <Col>
                <AppInput
                  placeholder={`${
                    isAdvancedOrSimplified ? "Additional " : ""
                  }Max Power`}
                  type="number"
                  errors={Error}
                  label={`Add ${
                    isAdvancedOrSimplified ? "Additional " : ""
                  }Max Power`}
                  isRequired={false}
                  value={value}
                  onChange={(event) => {
                    setValue(event.target.value);
                  }}
                />
              </Col>
            </Row>
          </>
        );
      case EEnergyNeedEditParameter.DISCHARGE_RATE:
        return (
          <Row className="mt-4">
            <Col>
              <AppInput
                placeholder="Discharge Rate"
                name="dischargeRate"
                type="number"
                errors={{
                  dischargeRate: {
                    message: error,
                  },
                }}
                label="Discharge Rate"
                isRequired={false}
                value={value}
                onChange={(event) => {
                  if (!event.target.value.trim()) {
                    setError("Discharge rate cannot be empty.");
                  } else if (
                    Number(event.target.value) < 0 ||
                    Number(event.target.value) > 100
                  ) {
                    setError("Discharge rate must be between 0 and 100.");
                  } else {
                    setError("");
                  }
                  setValue(event.target.value);
                }}
              />
            </Col>
          </Row>
        );
      case EEnergyNeedEditParameter.NIGHT_LOAD:
        return (
          <Row className="mt-4">
            <Col>
              <AppInput
                placeholder="Night Load"
                name="nightLoad"
                type="number"
                errors={{
                  nightLoad: {
                    message: error,
                  },
                }}
                label="Night Load"
                isRequired={false}
                value={value}
                onChange={(event) => {
                  if (!event.target.value.trim()) {
                    setError("Night load cannot be empty.");
                  } else if (
                    Number(event.target.value) < 0 ||
                    Number(event.target.value) > 100
                  ) {
                    setError("Night load must be between 0 and 100.");
                  } else {
                    setError("");
                  }
                  setValue(event.target.value);
                }}
              />
            </Col>
          </Row>
        );
      case EEnergyNeedEditParameter.SUNLIGHT_HOURS:
        return (
          <Row className="mt-3">
            <Col>
              <RangeSlider
                label="Daily Sunlight"
                unit="hours"
                min={4}
                max={6}
                step={0.5}
                defaultValue={Number(value)}
                onChange={(value) => {
                  setValue(String(value));
                }}
              />
            </Col>
          </Row>
        );
      case EEnergyNeedEditParameter.NO_OF_USERS:
        return (
          <Row className="mt-4">
            <Col>
              <AppInput
                placeholder="No. Of Users"
                name="noOfUsers"
                type="number"
                errors={{
                  waterDemand: {
                    message: error,
                  },
                }}
                label="No. Of Users"
                isRequired={false}
                value={value}
                onChange={(event) => {
                  const value = event.target.value;

                  if (!value.trim()) {
                    setError("No. Of Users cannot be empty.");
                  } else {
                    setError("");
                  }

                  if (value === "" || Number(value) > 0) {
                    setValue(value);
                  }
                }}
              />
            </Col>
          </Row>
        );
      case EEnergyNeedEditParameter.DEMAND_PER_USER:
        return (
          <Row className="mt-4">
            <Col>
              <AppInput
                placeholder="Demand per User"
                name="demandPerUser"
                type="number"
                errors={{
                  demandPerUser: {
                    message: error,
                  },
                }}
                label="Demand per User"
                isRequired={false}
                value={value}
                onChange={(event) => {
                  const value = event.target.value;

                  if (value === "" || Number(value) > 0) {
                    setValue(value);
                  }
                }}
              />
            </Col>
          </Row>
        );
      case EEnergyNeedEditParameter.AUTONOMY_DAYS:
        return (
          <Row className="mt-4">
            <Col>
              <AppInput
                placeholder="Autonomy Days"
                name="autonomyDays"
                type="number"
                errors={{
                  autonomyDays: {
                    message: error,
                  },
                }}
                label="Autonomy Days"
                isRequired={false}
                value={value}
                onChange={(event) => {
                  const value = event.target.value;

                  if (value === "" || Number(value) > 0) {
                    setValue(value);
                  }
                }}
              />
            </Col>
          </Row>
        );
    }
  };

  return (
    <ModalContainer
      show={show}
      onCancel={onCancel}
      onClose={onClose}
      onConfirm={() => {
        if (type === EEnergyNeedEditParameter.TITLE && !value.trim()) {
          setError("Title cannot be empty.");
          return;
        }

        if (
          type === EEnergyNeedEditParameter.DISCHARGE_RATE ||
          type === EEnergyNeedEditParameter.NIGHT_LOAD
        ) {
          if (!value.trim()) {
            setError(
              `${
                type === EEnergyNeedEditParameter.DISCHARGE_RATE
                  ? "Discharge rate"
                  : "Night load"
              } cannot be empty.`,
            );
            return;
          }

          if (Number(value) < 0 || Number(value) > 100) {
            setError(
              `${
                type === EEnergyNeedEditParameter.DISCHARGE_RATE
                  ? "Discharge rate"
                  : "Night load"
              } must be between 0 and 100.`,
            );
            return;
          }
        }

        if (error) {
          return;
        }
        onEditParameter(type, value);
      }}
      {...rest}
      title={getTitle(type)}
    >
      {getView(type)}
    </ModalContainer>
  );
};

export default EditParameterModal;
